import { useCallback } from 'react';

import { useAiOriginalImageQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useNativeShareHandler,
  useTranslate,
} from 'src/shared/hooks';
import { useHasValidNativeShare } from 'src/shared/hooks/useNativeShareHandler';
import { Route as Routes } from 'src/shared/route';
import { urlWithImageShare } from 'src/shared/utils/routing';

import { HomeownerAppMenuNames } from '../providers/HomeownerAppProvider';
import type { AiOrSampleGeneratedImage } from '../types';
import { isSampleGenerated } from '../types';

import { useCopyShareHandler, useShareToast } from './useCopyShareHandler';
import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseShareGeneratedImageOptions {
  image: AiOrSampleGeneratedImage;
}

type AiGeneratedImage = {
  aiOriginalImageId: string | number;
};

type AiOriginalImage = {
  originalImagePath: string;
};

type AiImageData = {
  aiOriginalImage: AiOriginalImage;
};

export const useShareGeneratedImage = ({
  image,
}: UseShareGeneratedImageOptions) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { href } = useLocation();

  const { setCurrentMenu } = useHomeownerAppContext();
  const { aiOriginalImageId } = image as AiGeneratedImage;
  const { data = {} } = useAiOriginalImageQuery({
    ssr: false,
    variables: {
      id: String(aiOriginalImageId),
    },
  });
  const { aiOriginalImage } = data as AiImageData;
  const { originalImagePath } = aiOriginalImage || {};
  const { generatedImagePath } = image;

  const toast = useShareToast();
  const handleCopyShare = useCopyShareHandler();
  const onShareFail = useCallback(
    () =>
      toast({
        description: t('share.messages.fail'),
        status: 'error',
      }),
    [toast, t],
  );

  const isSampleImage = isSampleGenerated(image);

  const baseUrl = new URL(Routes.HOMEOWNER_APP_SHARE.path, href);
  const handleNativeShare = useNativeShareHandler({
    imageUrl: urlWithImageShare(
      baseUrl.href,
      generatedImagePath,
      originalImagePath,
    ),
    text: t('share.shareOnSocial'),
    title: isSampleImage ? undefined : image.style,
    onShareFail,
  });
  const hasValidNativeShare = useHasValidNativeShare();

  return useCallback(() => {
    if (isSampleImage) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
      return;
    }
    if (hasValidNativeShare) {
      handleNativeShare();
    } else {
      void handleCopyShare({ generatedImagePath, originalImagePath });
    }
  }, [
    generatedImagePath,
    handleCopyShare,
    handleNativeShare,
    isSampleImage,
    hasValidNativeShare,
    originalImagePath,
    setCurrentMenu,
  ]);
};
